/**
 * Shared navigation menu structure
 * Used by both ResponsiveLayout (side nav) and TopNavLayout (top nav)
 * This ensures consistency across different navigation layouts
 */

import {
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  ReceiptLong as SalesIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon,
  Campaign as AdvertisingIcon,
  Calculate as CalculateIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarMonth as CalendarIcon,
  Construction as ToolsIcon,
  Psychology as IntelligenceIcon,
  Search as SourcingIcon,
  AutoAwesome as AIIcon,
  Build as BuildIcon,
  CloudUpload as MigrationIcon,
  BusinessCenter as OperationsIcon,
  AdminPanelSettings as SystemIcon,
  ReceiptLong,
  AccountBalance as CreditIcon,
  AccountBalanceWallet as GovernmentIcon,
  Assignment as TollNoticeIcon,
  SwapHoriz as RegistrationTransferIcon,
  AccountBox as AccountIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  ContactPhone as ContactIcon,
  ShoppingCart as PurchaseOrderIcon,
  Receipt as OverheadExpenseIcon,
  Announcement as AnnouncementIcon,
  HelpOutline as HelpIcon,
  Backup as BackupIcon,
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  ChatBubble as InboxMessageIcon,
} from '@mui/icons-material';

export interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  standalone?: boolean;
  iconOnly?: boolean;
  tooltip?: string;
  children?: MenuItem[];
}

export const navigationMenuStructure: MenuItem[] = [
  // Standalone top-level inbox
  {
    text: 'Inbox',
    icon: <InboxMessageIcon fontSize="small" />,
    path: '/inbox',
    standalone: true,
  },
  // Grouped sections
  {
    text: 'Vehicles',
    icon: <CarIcon fontSize="small" />,
    children: [
      { text: 'Inventory', icon: <CarIcon fontSize="small" />, path: '/inventory' },
      { text: 'Advertising', icon: <AdvertisingIcon fontSize="small" />, path: '/advertising' },
      // TEMPORARILY HIDDEN: { text: 'Workshop', icon: <ToolsIcon fontSize="small" />, path: '/workshop' },
      { text: 'Purchase Orders', icon: <PurchaseOrderIcon fontSize="small" />, path: '/purchase-orders' },
      { text: 'Expenses', icon: <OverheadExpenseIcon fontSize="small" />, path: '/expenses' },
      { text: 'Bulk Pricing Update', icon: <AttachMoneyIcon fontSize="small" />, path: '/bulk-pricing-update' },
      { text: 'PPSR/Car History', icon: <ReceiptLong fontSize="small" />, path: '/ppsr-reports' },
      { text: 'Government Payments', icon: <GovernmentIcon fontSize="small" />, path: '/government-payments' },
    ]
  },
  {
    text: 'Sales',
    icon: <TrendingUpIcon fontSize="small" />,
    children: [
      { text: 'Sales', icon: <TrendingUpIcon fontSize="small" />, path: '/sales' },
      { text: 'Leads', icon: <PeopleIcon fontSize="small" />, path: '/customers' },
      // { text: 'Commission', icon: <AttachMoneyIcon fontSize="small" />, path: '/commission' },
    ]
  },
  {
    text: 'Operations',
    icon: <OperationsIcon fontSize="small" />,
    children: [
      // { text: 'Dialer', icon: <PhoneIcon fontSize="small" />, path: '/dialer' },
      { text: 'Calendar', icon: <CalendarIcon fontSize="small" />, path: '/calendar' },
      // TEMPORARILY HIDDEN: { text: 'Credits', icon: <CreditIcon fontSize="small" />, path: '/credits' },
      { text: 'Accounting', icon: <CalculateIcon fontSize="small" />, path: '/accounting' },
      { text: 'Bank Accounts', icon: <BankIcon fontSize="small" />, path: '/bank-accounts' },
      { text: 'Documents', icon: <ReceiptLong fontSize="small" />, path: '/documents' },
    ]
  },
  {
    text: 'Reports',
    icon: <ReportsIcon fontSize="small" />,
    children: [
      { text: 'Reports', icon: <ReportsIcon fontSize="small" />, path: '/reports' },
      { text: 'Standard Reports', icon: <ReceiptLong fontSize="small" />, path: '/standard-reports' },
      { text: 'Management Reports', icon: <BusinessIcon fontSize="small" />, path: '/management-reports' },
      { text: 'Toll Notice', icon: <TollNoticeIcon fontSize="small" />, path: '/toll-notice' },
      { text: 'Registration Transfer', icon: <RegistrationTransferIcon fontSize="small" />, path: '/registration-transfer' },
      { text: 'Registration Renewal', icon: <CalendarIcon fontSize="small" />, path: '/registration-renewal' },
      { text: 'Dealings Register', icon: <ReceiptLong fontSize="small" />, path: '/dealings-register' },
      { text: 'Log of Changes', icon: <ReceiptLong fontSize="small" />, path: '/log-of-changes' },
    ]
  }
];

/**
 * Helper function to check if a path is active
 */
export const isPathActive = (currentPath: string, itemPath: string): boolean => {
  if (itemPath === '/') {
    return currentPath === '/';
  }
  return currentPath.startsWith(itemPath);
};

/**
 * Helper function to find the current page title
 */
export const getCurrentPageTitle = (pathname: string): string => {
  for (const item of navigationMenuStructure) {
    if (item.path && isPathActive(pathname, item.path)) {
      return item.text;
    }
    if (item.children) {
      for (const child of item.children) {
        if (child.path && isPathActive(pathname, child.path)) {
          return child.text;
        }
      }
    }
  }
  return 'Dashboard';
};